/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  NonNullableFormBuilder,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Validators } from 'ngx-editor';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import {
  AnyoS3Buckets,
  FileUtilsService,
} from 'projects/app-core/src/service/fileService';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { Currencies } from '../../../models/Currencies';
import { Tax } from '../../../models/Tax';
import { AnyoClubPass } from '../../../models/events/AnyoClubPasses';
import {
  EventEntryFee,
  EventPaymentType,
} from '../../../models/events/AnyoEvents';
import { ForumTags } from '../../../models/events/AnyoForums';
import { CreateOrUpdateForumReqBody } from '../../../models/events/ForumsCreateOrUpdateRequest';
import { PartnerTable } from '../../../models/partnerTable';
import { CirclesService } from '../../../services/circles.service';
import { EventsService } from '../../../services/events.service';
import { MasterdataService } from '../../../services/masterdata.service';
import { PartnerService } from '../../../services/partner.service';

@Component({
  selector: 'app-crud-forum',
  templateUrl: './crud-forum.component.html',
  styleUrl: './crud-forum.component.scss',
})
export class CrudForumComponent extends AnyoComponent {
  loading: boolean = false;
  forumId: string = '';
  forumName: string = '';
  forumDescription: string = '';
  isEdit: boolean = false;
  viewMode = true;
  forever: boolean = false;
  isUnlimitedRegistration: boolean = true;
  eventCategories: {
    label: string;
    value: string;
  }[] = [];
  shareImage!: File | null;
  images: File[] = [];
  shareMessage = '';
  eventEntryFee: EventEntryFee[] = [];

  presenterRows: {
    presenterId: string | null;
    isPaid: boolean;
    amount: number | null;
  }[] = [{ presenterId: null, isPaid: false, amount: null }];

  validateForm: FormGroup;
  dateTimeFormat = 'dd-MM-yyyy hh:mm a';
  dateFormat = 'dd-MM-yyyy';
  languages: string[] = [];
  eventPaymentTypes: string[] = Object.values(EventPaymentType);
  circleExpertsList: {
    expertId: string;
    name: string;
  }[] = [];
  selectedExperts: string[] = [];
  imagesLinks: string[] = [];
  shareImageLink: string = '';
  active: boolean = true;
  maxRegistration!: number;
  videoLink: string = '';
  meetLink: string = '';
  videoFile!: File;
  videoThumnailFile!: File;
  videoThumbnail = '';
  squareImage: File | undefined;
  squareImageLink: string | undefined;
  // Preview state for single-image selectors
  shareImagePreviewUrl: string | null = null;
  squareImagePreviewUrl: string | null = null;
  // shareImageName: string | null = null;
  // squareImageName: string | null = null;
  // Preview state for banner images (multiple selected in one choose action)
  bannerImagesPreviewUrls: string[] = [];
  // bannerImagesNames: string[] = [];
  selectedBannerFiles: File[] = [];
  partners: PartnerTable[] = [];
  selectedPartners: string[] = [];
  corporateOnlyEvent: boolean = false;
  clubPasses: AnyoClubPass[] = [];
  testEvent: boolean = false;
  testRealPayment: boolean = false;
  isDiscussionAvailable: boolean = false;
  canUserPost: boolean = false;
  canExpertPost: boolean = false;

  constructor(
    auth: AuthService,
    private fb: NonNullableFormBuilder,
    private activatedRoute: ActivatedRoute,
    private masterData: MasterdataService,
    private eventService: EventsService,
    private toasterService: ToastService,
    private circleService: CirclesService,
    private router: Router,
    private partnerService: PartnerService,
    private fileService: FileUtilsService,
  ) {
    super(auth);
    this.validateForm = fb.group({
      selectedCategory: ['', [Validators.required]],
      forumName: ['', [Validators.required]],
      forumDescription: ['', [Validators.required]],
      visibleTime: [new Date(), [Validators.required]],
      forumStartDate: [new Date(), [Validators.required]],
      // eventEndDate: ['', [this.validateDateInFuture]],
      // selectedLanguages: [[''], [Validators.required]],
      eventPaymentType: ['', [Validators.required]],
      applicableClubPasses: [[]],
      isUnlimitedRegistration: [false],
      maximumRegistrations: [null],

      forever: [false],
      forumEndDate: [null],
      presenterRows: this.fb.array([]),
      selectedLanguages: [[]],
      registrationCutOff: [null],
    });

    this.validateForm.get('forever')?.valueChanges.subscribe((val) => {
      const endCtrl = this.validateForm.get('forumEndDate');
      if (val) {
        endCtrl?.reset();
        endCtrl?.disable();
      } else {
        endCtrl?.enable();
      }
    });

    this.validateForm
      .get('eventPaymentType')
      ?.valueChanges.subscribe((paymentType) => {
        if (paymentType === 'Free') {
          // Clear all entry fees for free events
          this.eventEntryFee = [];
        } else {
          // For paid events, ensure at least one entry fee exists
          if (this.eventEntryFee.length === 0) {
            this.addEntryFee();
          }
        }
      });
    this.validateForm.disable();
  }

  async ready(): Promise<void> {
    if (this.user) {
      this.pageLoading = true;
      const masterData = await lastValueFrom(this.masterData.masterDataList());
      this.languages = masterData.languages.map((value) => value.data);
      this.eventCategories = masterData.eventCategories.map((value) => {
        return {
          value: value.m_id,
          label: value.data,
        };
      });
      const circleMasterDataResponse = await lastValueFrom(
        this.circleService.getCircleMasterData(),
      );
      this.circleExpertsList = circleMasterDataResponse.circleExperts.map(
        (value) => {
          return {
            expertId: value.name,
            name: value._id,
          };
        },
      );
      await this.getClubPasses();
      const id = this.activatedRoute.snapshot.params['id'];
      this.forumId = id;
      this.partners = await this.partnerService.getAllActivePartners();
      if (id) {
        this.isEdit = true;
        this.viewMode = true;
        await this.getAndSetForum(id);
      } else {
        this.isEdit = false;
        this.viewMode = false;
        this.validateForm.enable();
        // this.loading = false
      }
      this.pageLoading = false;
    }
  }

  addEntryFee() {
    const eventEntryFee: EventEntryFee = {
      id: '',
      amount: 0,
      currency: Currencies.INR,
      markupPrice: 0,
      tax: [],
    };
    this.eventEntryFee.push(eventEntryFee);
  }

  addPresenterRow() {
    this.presenterRows.push({
      presenterId: null,
      isPaid: false,
      amount: null,
    });
  }

  filteredExperts(index: number) {
    const selected = this.presenterRows
      .map((r) => r.presenterId)
      .filter((x) => x !== null);

    return this.circleExpertsList.filter(
      (expert) =>
        !selected.includes(expert.name) ||
        this.presenterRows[index].presenterId === expert.name,
    );
  }

  removeRow(index: number) {
    this.presenterRows.splice(index, 1);
  }

  onPresenterChange(index: number) {
    const row = this.presenterRows[index];
    row.isPaid = false;
    row.amount = null;
  }

  deleteImage(index: number | undefined) {
    if (index != undefined) {
      this.imagesLinks.splice(index, 1);
    }
  }

  async getClubPasses() {
    this.eventService.getAllClubPasses().subscribe({
      next: (value) => {
        this.clubPasses = value;
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(errorBody.description);
      },
    });
  }

  validateDateInFuture: ValidatorFn = (control: AbstractControl) => {
    if (!control.value) {
      return {
        required: true,
      };
    }
    if (!moment(control.value as Date).isAfter(moment())) {
      return {
        error: true,
      };
    }
    return {};
  };

  editForm() {
    this.viewMode = false;
    this.validateForm.enable();
  }

  mapToPaymentType(paymentType: string) {
    switch (paymentType) {
      case 'FREE':
        return 'Free';
      case 'PAID':
        return 'Paid';
      case 'CORPORATE_PAID':
        return 'Corporate Paid';
      case 'RETAIL_PAID':
        return 'Retail Paid';
      default:
        throw 'Invalid Event payment type passed';
    }
  }

  async handleMultipleFileUpload(files: File[]) {
    const uploadedFileUrls: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uuid = uuidv4();
        const uuidWithoutHyphens = uuid.replace(/-/g, '');
        const extension = file.name.split('.').pop();
        const filePath = `forums/${uuidWithoutHyphens}/images_.${extension}`;

        const uploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            file.type,
          ),
        );

        this.toasterService.showSuccess(`Uploading images ${i + 1}`);
        await lastValueFrom(this.fileService.uploadFile(file, uploadUrl.url));
        this.toasterService.showSuccess(`Uploading images ${i + 1} completed`);

        const fileUrl = `https://cdn.anyo.app/${filePath}`;
        uploadedFileUrls.push(fileUrl);
      }

      return uploadedFileUrls;
    } catch (e) {
      this.toasterService.showError(e + '');
      throw e;
    }
  }

  uploadImages($event: any) {
    const file = $event.target.files;
    if (file) {
      this.images = file;
      // Build previews for current selection; replace old previews
      // Revoke previous object URLs
      this.bannerImagesPreviewUrls.forEach((u) => {
        URL.revokeObjectURL(u);
      });
      this.bannerImagesPreviewUrls = [];
      // this.bannerImagesNames = [];
      const filesArray: File[] = Array.from(file as FileList);
      this.selectedBannerFiles = filesArray;
      for (const f of filesArray) {
        const url = URL.createObjectURL(f);
        this.bannerImagesPreviewUrls.push(url);
        // this.bannerImagesNames.push(f.name);
      }
    }
  }
  deleteBannerPreview(index: number) {
    if (index >= 0 && index < this.bannerImagesPreviewUrls.length) {
      const url = this.bannerImagesPreviewUrls[index];
      URL.revokeObjectURL(url);
      this.bannerImagesPreviewUrls.splice(index, 1);
      if (this.selectedBannerFiles && this.selectedBannerFiles.length > index) {
        this.selectedBannerFiles.splice(index, 1);
      }
    }
  }

  isFree = false;
  // IsFree(paymentType: string) {
  //   if (this.isFree) {
  //     if (paymentType == 'Free') {
  //       this.eventEntryFee = [];
  //     } else {
  //       this.eventEntryFee = [];
  //       this.addEntryFee();
  //     }
  //   }
  //   this.isFree = true;
  // }

  onPaymentTypeChange(paymentType: string): void {
    if (paymentType === 'Free') {
      // Clear all entry fees for free events
      this.eventEntryFee = [];
    } else {
      // For paid events, ensure at least one entry fee exists
      if (this.eventEntryFee.length === 0) {
        this.addEntryFee();
      }
    }
  }

  uploadShareImage($event: any) {
    const file = $event.target.files[0];
    if (file) {
      this.shareImage = file;
      // Set preview URL and name, replacing any existing preview
      if (this.shareImagePreviewUrl) {
        URL.revokeObjectURL(this.shareImagePreviewUrl);
      }
      this.shareImagePreviewUrl = URL.createObjectURL(file);
      // this.shareImageName = file.name;
    }
  }

  uploadThumbnail($event: any) {
    const file = $event.target.files[0];
    if (file) {
      this.videoThumnailFile = file;
    }
  }

  uploadThumbnailImage($event: any) {
    const file = $event.target.files[0];
    if (file) {
      this.squareImage = file;
      // Set preview URL and name, replacing any existing preview
      if (this.squareImagePreviewUrl) {
        URL.revokeObjectURL(this.squareImagePreviewUrl);
      }
      this.squareImagePreviewUrl = URL.createObjectURL(file);
      // this.squareImageName = file.name;
    }
  }

  getFileNameFromUrl(url: string | undefined): string | null {
    if (!url) return null;
    try {
      const u = new URL(url);
      const name = u.pathname.split('/').pop();
      return name || null;
    } catch {
      const name = url.split('/').pop();
      return name || null;
    }
  }

  disableEndDate = (current: Date): boolean => {
    const eventDate = this.validateForm.get('eventDate')?.value;

    if (!eventDate) {
      return false;
    }
    const eventMoment = moment(eventDate);
    return current && !moment(current).isAfter(eventMoment, 'day');
  };

  async checkImagesRatio(
    files: File | File[],
    square: boolean,
  ): Promise<boolean> {
    const processFile = async (file: File): Promise<boolean> => {
      if (!(file instanceof File)) {
        return false;
      }
      const img = new Image();
      img.src = URL.createObjectURL(file);

      return new Promise<boolean>((resolve) => {
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          const aspectRatio = width / height;
          const minAspectRatio = 1.9;
          const maxAspectRatio = 2.1;

          if (square) {
            if (Math.abs(aspectRatio - 1) > 0.1) {
              this.toasterService.showError(
                `Image ${file.name} is not square. Please upload a square image.`,
              );
              resolve(false);
              return;
            }
          } else {
            if (aspectRatio < minAspectRatio || aspectRatio > maxAspectRatio) {
              this.toasterService.showError(
                `Image ${file.name} is not in a valid 2:1 rectangular ratio. Please upload an image with an aspect ratio between 1.90 and 2.10.`,
              );
              resolve(false);
              return;
            }
          }

          URL.revokeObjectURL(img.src);
          resolve(true);
        };
      });
    };

    if (Array.isArray(files)) {
      for (const file of files) {
        const isValid = await processFile(file);
        if (!isValid) {
          return false;
        }
      }
      return true;
    } else {
      return processFile(files);
    }
  }
  onDiscussionAvailableChange(): void {
    if (!this.isDiscussionAvailable) {
      this.canUserPost = false;
      this.canExpertPost = false;
    }
  }

  async getAndSetForum(id: string): Promise<void> {
    // this.loading = true;
    this.eventService.getForum(id).subscribe({
      next: (forum) => {
        // Basic info
        this.forumName = forum.name;
        this.forumDescription = forum.description;
        this.active = forum.active;
        this.maxRegistration = forum.maxRegistrations || 0;

        // Configuration
        this.corporateOnlyEvent =
          (forum.partners && forum.partners.length > 0) || false;
        // this.selectedPartners =
        //   forum.partners?.map((partner) => partner.id) || [];
        //   ? forum.tags?.includes('RECURRING_EVENT')
        //   : false;

        // Media
        this.squareImageLink = forum.squareImage;
        this.imagesLinks = forum.bannerImages || [];
        this.shareImageLink = forum.shareImage || '';
        this.shareMessage = forum.shareMessage || '';
        // Initialize names from existing links for update preview
        // this.squareImageName = this.getFileNameFromUrl(this.squareImageLink);
        // this.shareImageName = this.getFileNameFromUrl(this.shareImageLink);

        // Payment
        if (forum.entryFee && forum.entryFee.length) {
          this.eventEntryFee = forum.entryFee;
        }

        // Experts and speakers
        this.selectedExperts = forum.presenterDetailIds || [];

        // Discussion flags
        this.isDiscussionAvailable =
          forum.tags?.includes(ForumTags.IS_DISCUSSION_AVAILABLE) || false;
        this.canUserPost =
          forum.tags?.includes(ForumTags.CAN_USER_POST) || false;
        this.canExpertPost =
          forum.tags?.includes(ForumTags.CAN_EXPERT_POST) || false;
        this.forever = forum.forumEndDate === null ? true : false;

        // Club passes
        const applicableClubPasses =
          forum.applicableClubPasses
            ?.map((data) =>
              this.clubPasses.find((d) => d.passId === data.passId),
            )
            ?.filter((pass) => pass !== undefined) || [];

        // Set form values
        this.validateForm.patchValue({
          selectedCategory: forum.categoryId,
          forumName: forum.name,
          forumDescription: forum.description,
          forumStartDate: forum.forumStartDate,
          forumEndDate: forum.forumEndDate ? forum.forumEndDate : null,
          visibleTime: forum.visibleFrom,
          eventPaymentType: this.mapToPaymentType(forum.paymentType),
          applicableClubPasses,
          maximumRegistrations: forum.maxRegistrations || 0,
          selectedLanguages: forum.language?.split(',') || [],
          registrationCutOff: forum.registrationCutOffDate || null,
          forever: forum.forumEndDate === null,
        });

        // -------------- SET PRESENTER ROWS --------------------
        this.presenterRows = []; // clear first

        // If API has presenterDetailIds but no payoutDetails → unpaid presenters
        const presenterIds = forum.presenterDetailIds || [];
        const payoutMap = new Map();

        // Convert payoutDetails to map for quick lookup
        if (forum.payoutDetails && forum.payoutDetails.length) {
          forum.payoutDetails.forEach((p) => {
            payoutMap.set(p.presenterId, p.payout.amount);
          });
        }

        // Build rows
        presenterIds.forEach((id) => {
          const paidAmount = payoutMap.get(id);

          this.presenterRows.push({
            presenterId: id,
            isPaid: paidAmount !== undefined,
            amount: paidAmount ?? null,
          });
        });

        // If no presenters found, ensure at least 1 empty row
        if (this.presenterRows.length === 0) {
          this.presenterRows.push({
            presenterId: null,
            isPaid: false,
            amount: null,
          });
        }
        this.selectedPartners =
          forum.partners?.map((partner) => partner.id) || [];

        this.testEvent = forum.tags?.includes(ForumTags.TEST_FORUM) || false;
        this.testRealPayment =
          forum.tags?.includes(ForumTags.TEST_REAL_PAYMENT) || false;
        this.validateForm.disable();
      },
      error: (error) => {
        // this.loading = false;
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(
          errorBody.description || 'Failed to load forum',
        );
      },
      complete: () => {
        // this.loading = false;
      },
    });
  }

  async addOrUpdateForum(): Promise<void> {
    if (!this.validateForm.valid) {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          this.toasterService.showError(
            'Check Any Field has got Error Message',
          );
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    const formValue = this.validateForm.value;

    if (
      this.eventEntryFee.some((value) => value.amount <= 0) &&
      formValue['eventPaymentType'] != 'Free'
    ) {
      this.toasterService.showAnyoErrorToast('Entry Fee should be greater 0');
      return;
    }

    const validPresenters = this.presenterRows.filter(
      (x) => x.presenterId !== null,
    );

    if (!validPresenters.length) {
      this.toasterService.showAnyoErrorToast(
        'Please select at least one Presenter',
      );
      return;
    }

    const invalidPayouts = this.presenterRows.filter(
      (x) => x.presenterId && x.isPaid && (!x.amount || x.amount <= 0),
    );

    if (invalidPayouts.length > 0) {
      this.toasterService.showAnyoErrorToast(
        'Please enter payout amount for presenters marked as paid',
      );
      return;
    }

    if (
      !this.validateForm.get('forever')?.value &&
      !formValue['forumEndDate']
    ) {
      this.toasterService.showAnyoErrorToast(
        'Forever is off. Please set an end date',
      );
      return;
    }

    const entryFee = this.eventEntryFee.map((value) => {
      const fee: EventEntryFee = {
        id: value.id,
        currency: Currencies.INR,
        markupPrice: value.markupPrice,
        amount: value.amount,
        tax: [] as Tax[],
      };
      return fee;
    });

    if (this.corporateOnlyEvent && !this.selectedPartners) {
      this.toasterService.showAnyoErrorToast(
        'Please select at-least one partner for corporate event',
      );
      return;
    }
    try {
      const validRows = this.presenterRows.filter((x) => x.presenterId);

      const presenterDetailIds = validRows.map((x) => x.presenterId!);

      const payoutDetails = validRows
        .filter((x) => x.isPaid)
        .map((x) => ({
          presenterId: x.presenterId!,
          payout: {
            currency: Currencies.INR,
            currencySymbol: '₹',
            amount: x.amount!,
          },
        }));

      const forum: CreateOrUpdateForumReqBody = {
        active: this.active,
        categoryId: formValue['selectedCategory'] as string,
        name: formValue['forumName'] as string,
        description: formValue['forumDescription'] as string,
        entryFee: entryFee,
        forumStartDate: moment(formValue['forumStartDate']).valueOf(),
        // forumEndDate :
        language: Array.isArray(formValue['selectedLanguages'])
          ? formValue['selectedLanguages'].join(',')
          : '',

        paymentType: formValue['eventPaymentType'] as string,
        presenterDetailIds: presenterDetailIds,
        // registrationCutOffDate : this.regis
        visibleFrom: moment(formValue['visibleTime']).startOf('day').valueOf(),
        bannerImages: this.imagesLinks,
        // bannerImages: [
        //   'https://cdn.anyo.app/events/5a3993a4ab50485284fe38b763bf8a52/images_.png',
        // ],

        shareImage: this.shareImageLink,
        // 'https://cdn.anyo.app/events/5a3993a4ab50485284fe38b763bf8a52/images_.png',
        shareMessage: this.shareMessage,
        maxRegistrations: this.maxRegistration || 0,
        partnerIds: this.selectedPartners,
        squareImage: this.squareImageLink,
        // 'https://cdn.anyo.app/events/5a3993a4ab50485284fe38b763bf8a52/squareImage_.png',
        isDiscussionAvailable: this.isDiscussionAvailable,
        canExpertPost: this.canExpertPost,
        canUserPost: this.canUserPost,
        applicableClubPasses:
          formValue['applicableClubPasses'].length > 0
            ? formValue['applicableClubPasses']
            : [],
        forever: formValue['forever'],
        testEvent: this.testEvent,
        testRealPayment: this.testRealPayment,
      };
      if (formValue['forumEndDate']) {
        forum.forumEndDate = moment(formValue['forumEndDate']).valueOf();
      }
      if (formValue['registrationCutOff']) {
        forum.registrationCutOffDate = moment(formValue['registrationCutOff'])
          .endOf('day')
          .valueOf();
      }
      if (payoutDetails.length > 0) {
        forum.payoutDetails = payoutDetails;
      }
      this.loading = true;
      if (this.images && this.images.length) {
        const filesArray: File[] = Array.isArray(this.images)
          ? this.images
          : Array.from(this.images);

        if (!(await this.checkImagesRatio(filesArray, false))) {
          this.loading = false;
          return;
        }
        const imagesArray = await this.handleMultipleFileUpload(this.images);
        this.imagesLinks.push(...imagesArray);
      }
      if (this.squareImage) {
        if (!(await this.checkImagesRatio(this.squareImage, true))) {
          this.loading = false;
          return;
        }
        const uuid = uuidv4();
        const extension = this.squareImage.name.split('.').pop();
        const uuidWithoutHyphens = uuid.replace(/-/g, '');
        const filePath = `events/${uuidWithoutHyphens}/squareImage_.${extension}`;
        const squareImageUploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            this.squareImage.type,
          ),
        );
        this.toasterService.showSuccess(`Uploading Square Image`);
        await lastValueFrom(
          this.fileService.uploadFile(
            this.squareImage,
            squareImageUploadUrl.url,
          ),
        );
        this.toasterService.showSuccess(`Uploading Square Image completed`);
        forum.squareImage = `https://cdn.anyo.app/${filePath}`;
      }
      if (this.shareImage) {
        if (!(await this.checkImagesRatio(this.shareImage, false))) {
          this.loading = false;
          return;
        }
        const uuid = uuidv4();
        const uuidWithoutHyphens = uuid.replace(/-/g, '');
        const extension = this.shareImage.name.split('.').pop();
        const filePath = `events/${uuidWithoutHyphens}/shareimage_.${extension}`;

        const uploadUrl = await lastValueFrom(
          this.fileService.generateUploadUrl(
            AnyoS3Buckets.CDN_BUCKET,
            filePath,
            this.shareImage.type,
          ),
        );

        this.toasterService.showSuccess(`Uploading shareImage`);
        await lastValueFrom(
          this.fileService.uploadFile(this.shareImage, uploadUrl.url),
        );
        this.toasterService.showSuccess(`Uploading shareImage completed`);

        forum.shareImage = `https://cdn.anyo.app/${filePath}`;
      }
      // comment for local check

      if (this.isEdit) {
        forum.forumId = this.forumId;
      }

      this.eventService.addOrUpdateForum(forum).subscribe({
        next: () => {
          this.loading = false;
          this.toasterService.showSuccess(
            `Forum ${this.isEdit ? 'Updated' : 'Created'} Successfully`,
          );
          this.router.navigate(['events/forums']);
        },
        error: (error) => {
          this.loading = false;
          const errorBody = error.error as IAnyoError;
          this.toasterService.showAnyoErrorToast(errorBody.description);
        },
      });
    } catch (error) {
      this.loading = false;
      this.toasterService.showError('An error occurred while saving the forum');
      console.error('Forum save error:', error);
    }
  }
}
